import React, { useEffect, useRef, useState } from "react";
import api, { apiError } from "@/lib/api";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Input } from "@/components/ui/input";
import { Paperclip, AlertTriangle } from "lucide-react";
import { toast } from "sonner";
import { ITEM_LABEL } from "@/components/DamageExtras";

const CHUNK = 4 * 1024 * 1024;
export const SEVERITY_TONE = { minor: "bg-amber-50 text-amber-800 border-amber-100", moderate: "bg-orange-50 text-orange-800 border-orange-100", severe: "bg-rose-50 text-rose-800 border-rose-100" };
export const STATUS_LABEL = { draft: "Draft", open: "Open", charged_to_deposit: "Charged to deposit", resolved: "Resolved" };

export async function uploadMedia(reportId, file, onProgress) {
  const total = Math.max(1, Math.ceil(file.size / CHUNK));
  const uploadId = Math.random().toString(36).slice(2, 12).replace(/[^a-z0-9]/gi, "x");
  for (let i = 0; i < total; i++) {
    const fd = new FormData();
    fd.append("upload_id", uploadId); fd.append("index", i); fd.append("total", total);
    fd.append("filename", file.name); fd.append("content_type", file.type || "application/octet-stream");
    fd.append("chunk", file.slice(i * CHUNK, (i + 1) * CHUNK), file.name);
    await api.post(`/damage-reports/${reportId}/media`, fd, { headers: { "Content-Type": "multipart/form-data" } });
    onProgress?.((i + 1) / total);
  }
}

export function DamageReportDialog({ task, onClose }) {
  const [desc, setDesc] = useState("");
  const [severity, setSeverity] = useState("minor");
  const [cost, setCost] = useState("");
  const [item, setItem] = useState("other");
  const [files, setFiles] = useState([]);
  const [progress, setProgress] = useState(null);
  const input = useRef(null);
  const submit = async () => {
    const big = files.find((f) => f.size > 100 * 1024 * 1024);
    if (big) { toast.error(`${big.name} is over 100 MB`); return; }
    setProgress(0);
    try {
      const { data } = await api.post("/damage-reports", { task_id: task.id, description: desc, severity, item, estimated_cost: cost ? Number(cost) : null });
      for (let k = 0; k < files.length; k++) {
        await uploadMedia(data.id, files[k], (p) => setProgress((k + p) / files.length));
      }
      toast.success("Damage reported — the office has been emailed");
      onClose();
    } catch (err) { toast.error(apiError(err)); setProgress(null); }
  };
  return (
    <Dialog open onOpenChange={(v) => !v && progress === null && onClose()}>
      <DialogContent className="max-w-lg" data-testid="damage-report-dialog" aria-describedby={undefined}>
        <DialogHeader><DialogTitle className="font-serif flex items-center gap-2"><AlertTriangle className="w-5 h-5 text-rose-600" /> Report guest damage</DialogTitle></DialogHeader>
        <div className="text-xs text-slate-500 -mt-2">{task.property_name} · {task.date}{task.guest_name ? ` · after ${task.guest_name}` : ""}</div>
        <Textarea rows={4} placeholder="What's damaged, where, and anything missing…" value={desc} onChange={(e) => setDesc(e.target.value)} data-testid="damage-description" />
        <div className="grid grid-cols-2 gap-2">
          <select className="h-10 rounded-md border border-slate-200 bg-white px-3 text-sm" value={severity} onChange={(e) => setSeverity(e.target.value)} data-testid="damage-severity">
            <option value="minor">Minor — wear, stains</option>
            <option value="moderate">Moderate — needs repair</option>
            <option value="severe">Severe — urgent / unsafe</option>
          </select>
          <Input type="number" min={0} step="0.01" placeholder="Est. cost £ (optional)" value={cost} onChange={(e) => setCost(e.target.value)} data-testid="damage-cost" />
          <select className="col-span-2 h-10 rounded-md border border-slate-200 bg-white px-3 text-sm" value={item} onChange={(e) => setItem(e.target.value)} data-testid="damage-item">
            {Object.entries(ITEM_LABEL).map(([v, l]) => <option key={v} value={v}>What's damaged: {l}</option>)}
          </select>
        </div>
        <input ref={input} type="file" accept="image/*,video/*" multiple className="hidden" data-testid="damage-media-input"
          onChange={(e) => setFiles([...files, ...Array.from(e.target.files || [])])} />
        <button type="button" onClick={() => input.current?.click()} data-testid="damage-media-add"
          className="flex items-center gap-2 text-sm text-teal-700 hover:text-teal-900 transition-colors"><Paperclip className="w-4 h-4" /> Add photos or videos (up to 100 MB each)</button>
        {files.length > 0 && <ul className="text-xs text-slate-600" data-testid="damage-media-list">{files.map((f, i) => <li key={i}>{f.name} · {(f.size / 1048576).toFixed(1)} MB</li>)}</ul>}
        {progress !== null && <div className="h-1.5 rounded-full bg-slate-100 overflow-hidden"><div className="h-full bg-teal-600 transition-[width]" style={{ width: `${Math.round(progress * 100)}%` }} /></div>}
        <Button className="w-full rounded-full ns-btn-primary" disabled={!desc.trim() || progress !== null} onClick={submit} data-testid="damage-submit">
          {progress !== null ? `Uploading ${Math.round(progress * 100)}%…` : "Send report"}
        </Button>
      </DialogContent>
    </Dialog>
  );
}

export function MediaThumb({ reportId, m }) {
  const [src, setSrc] = useState(null);
  useEffect(() => {
    let url;
    api.get(`/damage-reports/${reportId}/media/${m.id}`, { responseType: "blob" }).then((r) => { url = URL.createObjectURL(r.data); setSrc(url); }).catch(() => {});
    return () => url && URL.revokeObjectURL(url);
  }, [reportId, m.id]);
  if (!src) return <div className="w-24 h-24 rounded-lg bg-slate-100 animate-pulse" />;
  return m.kind === "video"
    ? <video src={src} controls className="w-40 h-24 rounded-lg bg-black object-cover" data-testid={`damage-media-${m.id}`} />
    : <a href={src} target="_blank" rel="noreferrer"><img src={src} alt={m.filename} className="w-24 h-24 rounded-lg object-cover" data-testid={`damage-media-${m.id}`} /></a>;
}

export function ContactDamageReports({ contactId }) {
  const [rows, setRows] = useState([]);
  useEffect(() => { api.get("/damage-reports", { params: { contact_id: contactId } }).then((r) => setRows(r.data)).catch(() => {}); }, [contactId]);
  if (!rows.length) return null;
  return (
    <section className="ns-card p-6 mt-6" data-testid="contact-damage-reports">
      <div className="font-serif text-xl mb-3 flex items-center gap-2"><AlertTriangle className="w-4 h-4 text-rose-600" /> Damage reported after this guest's stays</div>
      {rows.map((r) => (
        <div key={r.id} className="border-t border-slate-100 py-3">
          <div className="flex flex-wrap items-center gap-2 text-xs mb-1">
            <span className={`px-2 py-0.5 rounded-full border ${SEVERITY_TONE[r.severity]}`}>{r.severity}</span>
            <span className="text-slate-500">{r.property_name} · {r.stay}</span>
            <span className="text-slate-700">{STATUS_LABEL[r.status]}</span>
            {r.estimated_cost ? <span className="font-mono">£{r.estimated_cost}</span> : null}
          </div>
          <p className="text-sm text-slate-700">{r.description}</p>
          <div className="flex flex-wrap gap-2 mt-2">{r.media.map((m) => <MediaThumb key={m.id} reportId={r.id} m={m} />)}</div>
        </div>
      ))}
    </section>
  );
}
